import type { ProcessingJob } from "features/dt-plus-setup/model/types";

import { CustomModal } from "components/CustomModal";
import { useEffect, useRef, useState } from "react";

import { useTransientRequestForm } from "../hooks/useTransientRequestForm";
import { useTriggerTransientJob } from "../hooks/useTriggerTransientJob";
import { TRANSIENT_STEP, TransientStep } from "../model/constants";
import { TransientConfigurationFields } from "./TransientConfigurationFields";
import { TransientResponseActions } from "./TransientResponseActions";
import { TransientResponseStepper } from "./TransientResponseStepper";
import { TransientScenarioStep } from "./TransientScenarioStep";

type TransientRequestModalProps = {
  onJobStarted: (job: ProcessingJob) => void;
  open: boolean;
  setOpen: (open: boolean) => void;
};

const toTransientStep = (value: number): TransientStep => {
  if (value <= TRANSIENT_STEP.scenario) return TRANSIENT_STEP.scenario;
  return TRANSIENT_STEP.configuration;
};

export const TransientRequestModal = ({ onJobStarted, open, setOpen }: TransientRequestModalProps) => {
  const [stepNo, setStepNo] = useState<TransientStep>(TRANSIENT_STEP.scenario);
  const requestForm = useTransientRequestForm();
  const { reset } = requestForm;
  const resetRef = useRef(reset);
  const refetchScenariosRef = useRef(requestForm.scenarios.refetch);
  resetRef.current = reset;
  refetchScenariosRef.current = requestForm.scenarios.refetch;
  const isLastStep = stepNo === TRANSIENT_STEP.configuration;
  const { isTriggering, trigger } = useTriggerTransientJob({
    onJobStarted,
    onSuccess: () => setOpen(false),
    requestForm,
  });

  useEffect(() => {
    if (!open) return;
    setStepNo(TRANSIENT_STEP.scenario);
    resetRef.current();
    refetchScenariosRef.current();
  }, [open]);

  return (
    <CustomModal
      className="overflow-hidden !rounded-ds-1-5x !text-left"
      closeIcon={false}
      height="95vh"
      modalOpen={open}
      padding="0"
      setModalOpen={() => {}}
      width="100rem"
    >
      <div className="grid h-full grid-rows-[auto_1fr_auto]">
        <TransientResponseStepper
          changeStep={(nextStep) => setStepNo(toTransientStep(nextStep - 1))}
          currentStep={stepNo + 1}
        />
        <div className="overflow-y-auto p-8 px-10 pt-6">
          {stepNo === TRANSIENT_STEP.scenario ? (
            <TransientScenarioStep scenariosState={requestForm.scenarios} />
          ) : (
            <TransientConfigurationFields
              configuration={requestForm.configuration}
              scenarios={requestForm.scenarios.scenarios}
            />
          )}
        </div>
        <TransientResponseActions
          isLastStep={isLastStep}
          isTriggering={isTriggering}
          onBack={() => setStepNo((current) => toTransientStep(current - 1))}
          onCancel={() => setOpen(false)}
          onNext={() => setStepNo((current) => toTransientStep(current + 1))}
          onTrigger={trigger}
          showBack={stepNo > TRANSIENT_STEP.scenario}
        />
      </div>
    </CustomModal>
  );
};
